import React from "react";
import { styled } from "@mui/material/styles";
import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Button } from "@mui/material";

import { formatDuration, intervalToDuration, secondsToMilliseconds, format, Duration } from "date-fns";
import { ValidatedChildren } from "../../views/GeneralTask/generalTask.types";
import { Link } from "react-router-dom";

const PREFIX = "ChildTaskTable";

const classes = {
  success: `${PREFIX}-success`,
  failure: `${PREFIX}-failure`,
  warning: `${PREFIX}-warning`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.success}`]: {
    color: theme.palette.success.main,
  },

  [`& .${classes.failure}`]: {
    color: theme.palette.error.main,
  },

  [`& .${classes.warning}`]: {
    color: theme.palette.warning.main,
  },
}));

const StyledTable = styled(Table)(() => ({
  tableLayout: "fixed",
  wordWrap: "break-word",
}));

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
}));

const AlternatingRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const formatShortDuration = (duration: Duration) => {
  const digits = [
    String(duration.weeks ?? 0),
    String(duration.days ?? 0),
    String(duration.hours ?? 0).padStart(2, "0"),
    String(duration.minutes ?? 0).padStart(2, "0"),
    String(duration.seconds ?? 0).padStart(2, "0"),
  ].join(":");

  // trim leading zeros
  // leave a 0 if we trim it all
  return digits.replace(/^(0+:)+0?/, "") || "0";
};

export interface ChildTaskTableProps {
  data?: ValidatedChildren;
}

const ChildTaskTable: React.FC<ChildTaskTableProps> = ({ data }: ChildTaskTableProps) => {
  return (
    <StyledPaper>
      <StyledTable stickyHeader size="small">
        <colgroup>
          <col style={{ width: "35%" }} />
          <col style={{ width: "28%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "11%" }} />
        </colgroup>
        <TableHead>
          <AlternatingRow>
            <StyledTableCell>ID</StyledTableCell>
            <StyledTableCell>Created</StyledTableCell>
            <StyledTableCell>Duration</StyledTableCell>
            <StyledTableCell>Result</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
          </AlternatingRow>
        </TableHead>
        <TableBody>
          {data?.data
            ?.sort((e1, e2) => {
              if (e1?.__typename === "RuptureGenerationTask" && e2?.__typename === "RuptureGenerationTask") {
                const created1 = e1?.created ? (e1?.created as string) : "";
                const created2 = e2?.created ? (e2?.created as string) : "";
                return created1.localeCompare(created2);
              }
              return 0;
            })
            ?.map((e) => {
              if (
                e?.__typename === "RuptureGenerationTask" ||
                e?.__typename === "AutomationTask" ||
                e?.__typename === "OpenquakeHazardTask"
              ) {
                const duration =
                  e?.duration &&
                  intervalToDuration({
                    start: 0,
                    end: secondsToMilliseconds(e?.duration),
                  });
                return (
                  <AlternatingRow key={e?.id}>
                    <StyledTableCell>{e?.id}</StyledTableCell>
                    <StyledTableCell>
                      {e?.created ? format(new Date(e?.created as string), "PPPppp") : ""}
                    </StyledTableCell>
                    <StyledTableCell title={duration ? formatDuration(duration) : ""} style={{ textAlign: "right" }}>
                      {duration ? formatShortDuration(duration) : "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      <span className={e?.state === "DONE" ? classes.success : classes.warning}> {e?.state}</span>
                    </StyledTableCell>
                    <StyledTableCell>
                      <span className={e?.result === "SUCCESS" ? classes.success : classes.failure}> {e?.result}</span>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Button color="primary" variant={"outlined"} component={Link} to={`/Task/${e?.id}`}>
                        more
                      </Button>
                    </StyledTableCell>
                  </AlternatingRow>
                );
              }
              return <></>;
            })}
        </TableBody>
      </StyledTable>
    </StyledPaper>
  );
};

export default ChildTaskTable;
